// externals
import { createSelectorDeltaTelemetryMiddleware } from '@makemydeal/dr-shared-store';

// types
import type { Middleware } from 'redux';
import type { StateTree } from '@makemydeal/dr-dash-types';

// telemetry readiness / context (shared with stateSnapshotTelemetryMiddleware)
import { telemetryReady, getTelemetryBusinessContext } from './telemetryReadiness';

/**
 * Dash-specific selector-delta telemetry middleware (Rally US2040385).
 *
 * Emits `SelectorSnapshot` (session init + cadenced anchors) and
 * `SelectorDelta` (per-dispatch changes) events for every selector in the
 * registry (populated by consumers in follow-up stories — see Rally
 * US2040919 for the Deal Summary registrations).
 *
 * **Why a dash-specific wrapper?** Same pattern as `stateSnapshotTelemetryMiddleware`:
 * the shared factory has no knowledge of dash business context (dealXgId,
 * offerId). Other apps that adopt this middleware will write their own thin
 * wrappers with their own context extractors.
 *
 * **Telemetry control.** Server-side feature toggle `enableStateSnapshotTelemetry`
 * (shared with `stateSnapshotTelemetryMiddleware` — selector and state deltas
 * emit together as one coherent stream). Defaults to **OFF** at bootstrap; the
 * middleware short-circuits before any registry walk when the toggle is off.
 *
 * **Placement.** Must run immediately BEFORE `stateSnapshotTelemetryMiddleware`
 * (which remains last so it observes final post-reducer state). This ordering is
 * guaranteed structurally: `configureStore` appends the telemetry middlewares
 * after all business middleware (see `getTelemetryMiddleware`), covered by a
 * store-configurer unit test.
 *
 * For implementation details see:
 *   `@makemydeal/dr-shared-store/middleware/selectorDeltaTelemetryMiddleware`
 */
export const selectorDeltaTelemetryMiddleware: Middleware<
    Record<string, never>,
    StateTree
> = createSelectorDeltaTelemetryMiddleware<StateTree>({
    // Runtime feature toggle gate. Default OFF at bootstrap — telemetry ingest
    // and per-dispatch CPU cost is opt-in.
    //
    // Gated on the SAME toggle as `stateSnapshotTelemetryMiddleware`
    // (`enableStateSnapshotTelemetry`) so selector deltas and state snapshots
    // are one coherent stream — never one without the other.
    //
    // Reads the toggle slice DIRECTLY rather than via
    // `featureToggleSelectors.isStateSnapshotTelemetryEnabled(state)` on purpose:
    // that selector runs `featureTogglePreconditionCheck`, which THROWS when
    // called before bootstrap completes. This middleware evaluates `enabled` on
    // every dispatch — including the pre-bootstrap ones — so the selector would
    // throw-and-catch on the hot path for the whole early-session window. The
    // direct read is non-throwing and yields the same default-false result.
    // Kept identical to correlationMiddleware / stateSnapshotTelemetryMiddleware.
    // Type assertion: the toggle lives in AppConfigDataOnlyToggles which
    // StateTree types as Partial<AllFeatureToggles>.
    enabled: (state) => (state.featureToggles as Record<string, boolean | undefined>)?.enableStateSnapshotTelemetry ?? false,

    // Store identifier for cross-store correlation in replay tooling.
    storeId: 'dash-store',

    // Emit a full-registry snapshot every 100 non-ignored dispatches.
    // Deltas alone can be replayed forward from any snapshot; without them a
    // missed event breaks the rest of the session.
    snapshotCadence: 100,

    // Per-chunk byte cap. New Relic Browser silently drops any PageAction
    // string custom-attribute value above ~4096 chars, so we cap well below
    // to leave headroom for the other attributes in the envelope. Payloads
    // that exceed this cap are split into multiple rows sharing a
    // `chunkGroupId`; the replay CLI reassembles them by group id.
    maxChunkBytes: 3000,

    // Dash-specific business context — echoed on every emitted event so replay
    // tooling can group by deal/offer without joining against another table.
    // Shared with stateSnapshotTelemetryMiddleware so both streams carry
    // identical context. See telemetryReadiness.
    getBusinessContext: getTelemetryBusinessContext,

    // Readiness gate — shared with stateSnapshotTelemetryMiddleware so both
    // tail middlewares flip ready on the SAME dispatch and stay aligned on the
    // shared actionIndex join key. See telemetryReadiness for the rationale.
    waitForReady: telemetryReady
});
